import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { sortBy } from "lodash-es"
import * as API from "services/api"
import { errorToast, successToast } from "shared/toast";

const SLICE_NAME = "SAMLConfigurations"

const INITIAL_STATE = {
  configurations: [],
  loading: false,
}

let setOrganizationSubdomain

export const initialize = createAsyncThunk(
  `${SLICE_NAME}/initialize`,
  async ({ organizationSubdomain }, thunkAPI) => {
    thunkAPI.dispatch(setOrganizationSubdomain({ organizationSubdomain }))

    let configurations = []

    const response = await API.phpOrganizationSAMLConfigurations({ organizationSubdomain })

    if (response.ok) {
      configurations = response.data
    } else {
      throw new Error("Something went wrong. Unable to load SAML configurations.")
    }

    return (
      { configurations }
    )
  },
)

export const createSAMLConfiguration = createAsyncThunk(
  `${SLICE_NAME}/createSAMLConfiguration`,
  async ({ idpMetadataFile, name, onSuccess }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpCreateOrganizationSAMLConfiguration({ idpMetadataFile, name, organizationSubdomain })

    if (!response.ok) {
      throw Error("Something went wrong. Unable to create SAML configuration")
    }

    return (
      {
        configuration: response.data,
        onSuccess,
      }
    )
  },
)

export const deleteSAMLConfiguration = createAsyncThunk(
  `${SLICE_NAME}/deleteSAMLConfiguration`,
  async ({ id }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpDeleteOrganizationSAMLConfiguration({ id, organizationSubdomain })

    if (!response.ok) {
      throw Error("Something went wrong. Unable to delete SAML configuration")
    }

    return (
      { id }
    )
  },
)

export const disableSLO = createAsyncThunk(
  `${SLICE_NAME}/disableSLO`,
  async ({ id }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpOrganizationSAMLConfigurationDisableSLO({ id, organizationSubdomain })

    if (!response.ok) {
      throw Error("Something went wrong. Unable to disable single logout")
    }

    return (
      { configuration: response.data }
    )
  },
)

export const enableSLO = createAsyncThunk(
  `${SLICE_NAME}/enableSLO`,
  async ({ id }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpOrganizationSAMLConfigurationEnableSLO({ id, organizationSubdomain })

    if (!response.ok) {
      throw Error("Something went wrong. Unable to enable single logout")
    }

    return (
      { configuration: response.data }
    )
  },
)

export const rename = createAsyncThunk(
  `${SLICE_NAME}/rename`,
  async ({ id, name }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpRenameOrganizationSAMLConfiguration({ id, name, organizationSubdomain })

    if (!response.ok) {
      throw Error("Something went wrong. Unable to rename SAML configuration.")
    }

    return (
      { configuration: response.data }
    )
  },
)

export const updateIDPSettings = createAsyncThunk(
  `${SLICE_NAME}/updateIDPSettings`,
  async ({ id, idpMetadataFile }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpOrganizationSAMLConfigurationUpdateIDPSettings({
      id,
      idpMetadataFile,
      organizationSubdomain,
    })

    if (!response.ok) {
      throw Error("Something went wrong. Unable to update IDP settings.")
    }

    return (
      { configuration: response.data }
    )
  },
)

export const SAMLConfigurationsSlice = createSlice({
  name: SLICE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    setOrganizationSubdomain: (state, action) => {
      const { organizationSubdomain } = action.payload
      state.organizationSubdomain = organizationSubdomain
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initialize.pending, (state) => {
        state.loading = true
      })
      .addCase(initialize.fulfilled, (state, action) => {
        const { configurations } = action.payload

        state.configurations = sortBy(configurations, "name")
        state.loading = false
      })
      .addCase(initialize.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(createSAMLConfiguration.fulfilled, (state, action) => {
        const { configuration, onSuccess } = action.payload

        if (!configuration) return

        const updatedConfigurations = [...state.configurations, configuration]
        state.configurations = sortBy(updatedConfigurations, "name")
        onSuccess?.()
        successToast("SAML configuration created successfully.")
      })
      .addCase(createSAMLConfiguration.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(deleteSAMLConfiguration.fulfilled, (state, action) => {
        const { id } = action.payload
        state.configurations = state.configurations.filter((c) => c.id !== id)
        successToast("SAML configuration deleted successfully.")
      })
      .addCase(deleteSAMLConfiguration.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(disableSLO.fulfilled, (state, action) => {
        const { configuration } = action.payload

        if (!configuration) return

        const index = state.configurations.findIndex((c) => c.id === configuration.id)
        state.configurations[index] = configuration
      })
      .addCase(disableSLO.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(enableSLO.fulfilled, (state, action) => {
        const { configuration } = action.payload

        if (!configuration) return

        const index = state.configurations.findIndex((c) => c.id === configuration.id)
        state.configurations[index] = configuration
      })
      .addCase(enableSLO.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(rename.fulfilled, (state, action) => {
        const { configuration } = action.payload

        if (!configuration) return

        const index = state.configurations.findIndex((c) => c.id === configuration.id)
        state.configurations[index] = configuration
        successToast("SAML configuration renamed successfully.")
      })
      .addCase(rename.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(updateIDPSettings.fulfilled, (state, action) => {
        const { configuration } = action.payload

        if (!configuration) return

        const index = state.configurations.findIndex((c) => c.id === configuration.id)
        state.configurations[index] = configuration
        successToast("IDP settings updated successfully.")
      })
      .addCase(updateIDPSettings.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
  },
})

setOrganizationSubdomain = SAMLConfigurationsSlice.actions.setOrganizationSubdomain

export const configurations = (state) => state[SLICE_NAME].configurations

export const loading = (state) => state[SLICE_NAME].loading

export default SAMLConfigurationsSlice.reducer
